import {
    EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    getDomain,
    normalizeDataLayer,
    normalizeDiacriticsString,
    TagDataLayerPartial,
} from '@axa-fr-axion/tag';
import { ProfilClient } from '@axa-fr-axion/user-journey-services';
import { AppOptions, JourneyData } from 'App.interface';
import { getSelectedProducts } from 'App.utils';
import { Product } from '../catalog';
import { allProducts } from '../catalog/Catalog.data';
import { CatalogMap } from '../../features/catalog';
import { GenderOptions } from 'features/contact/identity/gender';
import { getProductCodes } from '../../features/pre-choose-advisor/PreChooseAdvisor.utils';
import { NavigationPath, StepNavigationPath } from 'interfaces';
import { DATA_LAYER_GLOBAL, getMicroEntNavigationData, getProEntNavigationData } from './Tag.data';
import { TagCivility } from './Tag.interface';
import hashSha from 'utils/hashSha';
import { OPTIONS_FOR_MICRO_ENTREPRENEUR } from 'features/legal-status/LegalStatus.data';
import { Preset } from 'presets';

export const SEND_TAG_EVENT_NAME = 'sendTag';
export const EXCLUDED_KEY_TO_NORMALIZE: Partial<keyof TagDataLayerPartial>[] = [
    ...EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    'code_du_produit',
    'protection_activite',
    'protection_dirigeant',
    'protection_salaries',
];
export const KEEPING_SPACE_KEY_TO_NORMALIZE: Partial<keyof TagDataLayerPartial>[] = [
    ...EXCLUDED_KEY_TO_NORMALIZE_COMMON,
    'form_contact_prenom_hash',
    'form_contact_nom_hash',
];

export const getHashedDataLayer = async (data: JourneyData, options?: AppOptions): Promise<TagDataLayerPartial> => {
    const fiscalJourneyDataLayer = journeyDataToDataLayer(data, options);
    const normalizedDataLayer = normalizeDataLayer(
        fiscalJourneyDataLayer,
        EXCLUDED_KEY_TO_NORMALIZE,
        KEEPING_SPACE_KEY_TO_NORMALIZE
    );
    return hashDataLayer(normalizedDataLayer, data);
};
export const isEligibleMPE = (data: Partial<JourneyData>, preset?: Preset) => {
    if (preset === 'MicroEnt') {
        return yesOrNo(
            !(data?.requestError?.status.funnel === 'HORS-CIBLE'
            || data?.requestError?.status.funnel === 'HORS-CIBLE-PRODUIT'
            || data?.folder?.lead?.funnel === 'HORS-CIBLE')
        );
    }

    return yesOrNo(OPTIONS_FOR_MICRO_ENTREPRENEUR.find(status => status.value === data.legalStatus)
        && data?.numberOfEmployees?.slice?.value === '00'
        && data?.income === 'Jusqu\'à 90 000 €');
};

const productCodesSeparator = '|';

const journeyDataToDataLayer = (data: JourneyData, options?: AppOptions): TagDataLayerPartial => {
    const {
        identity,
        homeAddress,
        folder,
        economicSector,
        legalStatus,
        numberOfEmployees,
        income,
        yearsOfExperience,
        hasEmployees,
        cumulativeValueOfGoods,
        businessPremisesPresence,
        businessPremisesOwnership,
        businessPremisesSurface,
        hasPeculiarities,
    } = data;

    const selectedProducts = getSelectedProducts(data) || new Map();

    return {
        code_du_produit: getProductCodesFromCatalogDatas(selectedProducts).join(productCodesSeparator),
        protection_activite: getProductCodesByCategorie('activite', selectedProducts).join(productCodesSeparator),
        protection_dirigeant: getProductCodesByCategorie('dirigeant', selectedProducts).join(productCodesSeparator),
        protection_salaries: getProductCodesByCategorie('salaries', selectedProducts).join(productCodesSeparator),
        secteur_d_activite: economicSector.sector?.label,
        forme_juridique: legalStatus,
        nombre_salaries: numberOfEmployees.slice?.label,
        chiffre_affaires: income?.toString(),
        annee_experience: yearsOfExperience?.toString(),
        statut_client: toProfileClientConversion(
            folder.personne?.profilClient
        ) as Lowercase<ProfilClient>,
        form_contact_ville: homeAddress.city,
        form_entreprise_ville: homeAddress.city,
        form_contact_codepostal: homeAddress.postalCode,
        form_contact_codepostal_hash: homeAddress.postalCode,
        form_entreprise_codepostal: homeAddress.postalCode,
        form_entreprise_codepostal_hash: homeAddress.postalCode,
        form_contact_civilite: toTagCivility(identity.gender),
        form_contact_nom_hash: identity.lastname,
        form_contact_prenom_hash: identity.firstname,
        form_contact_telephone_hash: identity.phone,
        form_contact_email_hash: identity.email?.toLowerCase(),
        nom_domaine_email: getDomain(identity.email?.toLowerCase()),
        type_hors_cible: '',
        form_numero_dossier: folder.lead?.id,
        form_numero_dossier_hash: folder.lead?.hash,
        lead_canal_traitement: '',
        salaries: hasEmployees,
        valeur_materiel: cumulativeValueOfGoods?.cumulativeValue,
        local_professionnel: businessPremisesPresence,
        locataire_proprietaire_local: businessPremisesOwnership,
        superficie_local: businessPremisesSurface?.surface,
        specificite_activites: hasPeculiarities,
        eligibilite_offre_MPE: isEligibleMPE(data, options?.config?.preset),
    };
};

export function getProductCodesFromCatalogDatas(selectedProducts: CatalogMap): ReadonlyArray<string> {
    return selectedProducts ? getProductCodes(selectedProducts) : [];
}

export function getProductCodesByCategorie(
    categorieProduit: string,
    selectedProducts: CatalogMap
): ReadonlyArray<string> {
    const productsToSearch = getProductsToSearch(selectedProducts);
    const filteredGroups = allProducts.groups.find(group => group.code === categorieProduit);

    if (productsToSearch && filteredGroups) {
        return productsToSearch
            .filter(product =>
                filteredGroups.products.some(groupProduct => groupProduct.codeProduit === product.codeProduit)
            )
            .map(product => product.codeProduit);
    }
    return [];
}

function getProductsToSearch(selectedProducts: CatalogMap): Product[] {
    return Array.from(selectedProducts.values()).flatMap(productSet => Array.from(productSet));
}

export const shouldSendTagEventNavigation = (step: NavigationPath, preset?: Preset): step is StepNavigationPath =>
    Boolean(getNavigationData(preset)[ step as StepNavigationPath ]);

export const hashDataLayer = async (
    data: TagDataLayerPartial,
    journeyData: JourneyData
): Promise<TagDataLayerPartial> => {
    const lastname = await hashSha(normalizeDiacriticsString(journeyData.identity.lastname));
    const firstname = await hashSha(data.form_contact_prenom_hash);
    const zipcodeContact = await hashSha(data.form_contact_codepostal);
    const hashnpc = (await hashSha(lastname + firstname + zipcodeContact))?.toUpperCase();

    return {
        ...data,
        form_contact_nom_hash: lastname,
        form_contact_prenom_hash: firstname,
        form_contact_codepostal_hash: zipcodeContact,
        form_entreprise_codepostal_hash: await hashSha(data.form_entreprise_codepostal as string),
        hashnpc,
        form_contact_telephone_hash: await hashSha(data.form_contact_telephone_hash),
        form_contact_email_hash: await hashSha(data.form_contact_email_hash),
        // The folder id is already hashed by the backend
        form_numero_dossier_hash: data.form_numero_dossier_hash,
    };
};

export const toTagCivility = (gender?: GenderOptions): TagCivility | '' => {
    switch (gender) {
        case 'ms':
            return 'madame';
        case 'mr':
            return 'monsieur';
        default:
            return '';
    }
};

export const yesOrNo = (value = false) => {
    return value ? 'oui' : 'non';
};

export const getDataLayerGlobal = (preset?: Preset) => {
    if (preset === 'MicroEnt') {
        return {
            ...DATA_LAYER_GLOBAL,
            nom_site: 'quickquote_micro_entrepreneur',
        };
    }
    return DATA_LAYER_GLOBAL;
};

export const getNavigationData = (preset?: Preset, funnel?: string) => {
    return preset === 'MicroEnt' ? getMicroEntNavigationData(funnel) : getProEntNavigationData(funnel);
};

export const toProfileClientConversion = (profil?: ProfilClient) => {
    if (profil === 'UnbeknownstClient') {
        return 'Client';
    } else {
        return profil;
    }
};
